import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePDF, Resolution } from 'react-to-pdf'
import { Button, Card, Col, Container, Form, FormControl, FormSelect, Row } from 'react-bootstrap'
import {
  CreditCardFill,
  FileEarmarkFill,
  FilePersonFill,
  Globe2,
  PersonFill,
  PersonVcardFill,
  PlusSquareFill,
} from 'react-bootstrap-icons'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import countryList from 'react-select-country-list'
import { useDebouncedCallback } from 'use-debounce'
import { HistoryModal } from '../components/HistoryModal'
import KAccess from '../components/KAccess'
import KConfirm from '../components/KConfirm'
import KLayerNavigation from '../components/KLayerNavigation'
import KSwitch from '../components/KSwitch'
import KDayPicker from '../components/KDayPicker'
import { ValidationModal } from '../components/ValidationModal'
import {
  apiRequest,
  canEditKycForms,
  formatDDMMYYYY,
  getDeep,
  getUserAccess,
  isUserLoggedIn,
  setAccessToken,
  setUserAccess,
  updateNestedField,
} from '../helpers'
import {
  createFieldUpdate,
  EMPTY_COMMUNICATION,
  emptyAddress,
  emptyBank,
  emptyBeziehung,
  emptyNaturalPersonData,
  emptyNaturalPersonLegitimation,
  extractDynamicAdditional,
  extractStammdatenRoot,
  normalizeAddresses,
  normalizeBanks,
  normalizeBeziehung,
  normalizeCommunication,
  normalizeFiles,
  toArray,
  uploadStammdatenFile,
} from '../helpers/stammdaten.helper'
import { getLocale, t } from '../locale'
import {
  createClient,
  fetchAccess,
  fetchClientById,
  updateStammdatenClient,
  validatePinCode,
  validatePinCodeClient,
} from '../service/client'
import { fetchServiceCosts, ServiceCosts } from '../service/subscription'
import { getBankCountries, getCountries } from '../service/country'
import {
  getAdelstitel,
  getAnrede,
  getBanksByCountry,
  getFederalState,
  getNamePrefix,
} from '../service/stammdaten'
import {
  AdditionalFormSchema,
  Communication,
  FileUpload,
  HistoryEntry,
  NaturalPersonData,
  NaturalPersonLegitimation,
  PersonForm,
} from '../types/stammdaten.type'
import {
  MemoizedStammdatenAddress,
  MemoizedStammdatenBank,
  MemoizedStammdatenBeziehungen,
  MemoizedStammdatenCommunication,
} from './StammdatenCompany'
import KSignModal from '../components/KSign'
import { downloadKycFile } from '../helpers/downloadKycFile'
import KFilePreview, { PreviewTarget } from '../components/KFilePreview'
import v from '../validators'

// Constants
const SECTION_KEYS = {
  PERSONAL: 'personal',
  LEGIT: 'legit',
  ADDRESS: 'address',
  COMMUNICATION: 'communication',
  BANK: 'bank',
  RELATIONSHIPS: 'relationships',
  DOCUMENTS_LAWYER: 'documentsLawyer',
  DOCUMENTS_CLIENT: 'documentsClient',
  ADDITIONAL: 'additional',
} as const

// Custom hooks
const usePersonData = (clientId: string) => {
  const [personData, setPersonData] = useState<PersonForm>({
    clientNumber: '',
    client_since: '',
    client_to: '',
    anrede: '',
    academic_degree: '',
    first_name: '',
    name: '',
    short_name: '',
    noble_title: '',
    name_prefix: '',
    alternative_search_name: '',
    client_note: '',

    natural_person_data: emptyNaturalPersonData,

    nationality: '',
    marital_status: '',
    national_law: {
      pension_insurance_no: '',
      pension_insurance_provider: '',
      eu_vat_id_country_code: '',
      eu_vat_id_number: '',
      eTIN: '',
      identification_number: '',
    },
    federal_state: '',

    legitimation_type: '',
    natural_person_legitimation: emptyNaturalPersonLegitimation(),

    address: [emptyAddress()],
    communication: EMPTY_COMMUNICATION,
    bank: [emptyBank()],
    beziehung: [emptyBeziehung()],
    additional: { dynamic: {} },
    files: [],
    filesClient: [],
  })

  const [requiredFields, setRequiredFields] = useState({
    client_since: false,
    client_to: false,
    anrede: false,
    academic_degree: false,
    first_name: false,
    name: false,
    short_name: false,
    noble_title: false,
    name_prefix: false,
    alternative_search_name: false,
    note: false,
    natural_person_data: {
      birth_date: false,
      birth_name: false,
      full_first_names: false,
      place_of_birth: false,
      country_of_birth: false,
      nationality: false,
    },
    natural_person_legitimation: {
      legitimation_type: false,
      comment: false,
      pep: false,
      id_number: false,
      birth_date: false,
      nationality: false,
      place_of_birth: false,
      valid_to: false,
      issued_on: false,
      issuing_authority: false,
    },
    address: {
      valid_since: false,
      valid_to: false,
      address_suffix: false,
      zip_code: false,
      city: false,
      country: false,
      shipping_supplement: false,
      note: false,
    },
    communication: {
      phone: false,
      email: false,
      url: false,
      other: false,
    },
    bank: {
      valid_since: false,
      valid_to: false,
      country: false,
      account_number: false,
      account_holder: false,
      note: false,
    },
    tax_authority: {
      valid_since: false,
      valid_to: false,
      country_code: false,
      tax_authority_name: false,
      tax_number: false,
      betriebsfinanzamt: false,
      umsatzsteuerfinanzamt: false,
      lohnsteuerfinanzamt: false,
    },
    beziehung: {
      hatIst: false,
      name: false,
      addressee_type: false,
      anrede: false,
      tax_authority: {
        country_code: false,
        tax_authority_name: false,
        tax_number: false,
      },
      address: {
        type: false,
        zip_code: false,
        city: false,
        country: false,
        phone: false,
        email: false,
      },
    },
  })

  const fetchRequiredFields = useCallback(async () => {
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API_URL}/stammdaten/requiredBy?type=person`,
        'GET'
      )
      if (res.ok) {
        res.result.fields.forEach((element: any) => {
          const fieldPath = element.name
          const value = element.required
          setRequiredFields(prev => updateNestedField(prev, fieldPath, value))
        })
      }
    } catch (error) {
      console.error('Failed to fetch required fields:', error)
    }
  }, [])

  useEffect(() => {
    if (clientId) {
      fetchRequiredFields()
    }
  }, [clientId, fetchRequiredFields])

  return { personData, setPersonData, requiredFields }
}

const useExpandedSections = () => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    [SECTION_KEYS.PERSONAL]: false,
    [SECTION_KEYS.LEGIT]: false,
    [SECTION_KEYS.ADDRESS]: false,
    [SECTION_KEYS.COMMUNICATION]: false,
    [SECTION_KEYS.BANK]: false,
    [SECTION_KEYS.RELATIONSHIPS]: false,
    [SECTION_KEYS.DOCUMENTS_LAWYER]: false,
    [SECTION_KEYS.DOCUMENTS_CLIENT]: false,
    [SECTION_KEYS.ADDITIONAL]: false,
  })

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }, [])

  const expandAllSections = useCallback(() => {
    setExpandedSections(prev =>
      Object.keys(prev).reduce(
        (acc, key) => ({
          ...acc,
          [key]: true,
        }),
        {} as Record<string, boolean>
      )
    )
  }, [])

  return { expandedSections, toggleSection, expandAllSections }
}

export default function StammdatenPerson() {
  const navigate = useNavigate()
  const { clientId } = useParams()

  // The shared-client view is reached through StammdatenPinAccess, which navigates
  // to the plain `/client/stammdaten/person/:clientId` route (no `:email` segment),
  // so it can only be told apart from an internal team member by the absence of a
  // logged-in user session. `clientEmail` is the address PIN access stored for them.
  const isSharedClient = !isUserLoggedIn()
  const clientEmail = localStorage.getItem('email') || ''

  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null)
  const [costConfirmed, setCostConfirmed] = useState(clientId !== 'new')

  const { toPDF, targetRef } = usePDF({ filename: 'stammdaten.pdf', resolution: Resolution.LOW })
  const formRef = useRef<HTMLFormElement>(null)
  const [searchParams] = useSearchParams()
  const pdf = (searchParams.get('pdf') ?? '').toLowerCase() === 'true'
  const pdfGeneratedRef = useRef(false)

  const { personData, setPersonData, requiredFields } = usePersonData(clientId || '')
  const { expandedSections, toggleSection, expandAllSections } = useExpandedSections()

  const canEdit = canEditKycForms()

  const [openSchemas, setOpenSchemas] = useState<Record<string, boolean>>({})
  const [additionalForms, setAdditionalForms] = useState<AdditionalFormSchema[]>([])
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const [anredeOptions, setAnredeOptions] = useState<any[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [nobleTitle, setNobleTitel] = useState<any[]>([])
  const [namePrefix, setNamePrefix] = useState<any[]>([])
  const [availableBanks, setAvailableBanks] = useState<any[]>([])
  const [countries, setCountries] = useState([])
  const [bankCountries, setBankCountries] = useState([])

  const [datePickerClientSince, setDatePickerClientSince] = useState(false)
  const [datePickerClientTo, setDatePickerClientTo] = useState(false)
  const [datePickerBirthDate, setDatePickerBirthDate] = useState(false)
  const [datePickerLegitimationValidTo, setDatePickerLegitimationValidTo] = useState(false)
  const [datePickerLegitimationIssuedOn, setDatePickerLegitimationIssuedOn] = useState(false)

  const datePickerRefClientSince = useRef<HTMLDivElement>(null)
  const datePickerRefClientTo = useRef<HTMLDivElement>(null)
  const datePickerRefBirthDate = useRef<HTMLDivElement>(null)
  const datePickerRefLegitimationValidTo = useRef<HTMLDivElement>(null)
  const datePickerRefLegitimationIssuedOn = useRef<HTMLDivElement>(null)

  const [access, setAccess] = useState(false)
  const [pin, setPin] = useState('')
  const [pinRequired, setPinRequired] = useState(true)
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null)

  const banks = useMemo(() => personData.bank.map((_, i) => i), [personData.bank])
  const beziehungs = useMemo(() => personData.beziehung.map((_, i) => i), [personData.beziehung])

  const creatingClientRef = useRef(false)
  const realClientIdRef = useRef<string | null>(null)

  const [signModal, setSignModal] = useState(false)
  const [pendingSignDocument, setPendingSignDocument] = useState<string | null>(null)
  const [currentDocument, setCurrentDocument] = useState('')
  const [accessForSign, setAccessForSign] = useState(false)
  const isClientSignRef = useRef(false)

  const [documentType, setDocumentType] = useState('')

  const nationalityOptions = useMemo(() => {
    const displayNames = new Intl.DisplayNames([getLocale()], { type: 'region' })
    return countryList()
      .getData()
      .map(c => ({ value: c.value, label: displayNames.of(c.value) ?? c.label }))
      .sort((a, b) => a.label.localeCompare(b.label, getLocale()))
  }, [])

  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
    title: '',
    entries: [] as HistoryEntry[],
    options: undefined as { value: any; label: string }[] | undefined,
  })

  const [validationModal, setValidationModal] = useState({
    agreed: false,
    isOpen: false,
    field: '',
  })

  useEffect(() => {
    fetchAllData()
  }, [clientId])

  useEffect(() => {
    if (clientId === 'new') {
      fetchServiceCosts().then(setServiceCosts)
    }
  }, [clientId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRefClientSince.current &&
        !datePickerRefClientSince.current.contains(event.target as Node)
      ) {
        setDatePickerClientSince(false)
      }
      if (
        datePickerRefClientTo.current &&
        !datePickerRefClientTo.current.contains(event.target as Node)
      ) {
        setDatePickerClientTo(false)
      }
      if (
        datePickerRefBirthDate.current &&
        !datePickerRefBirthDate.current.contains(event.target as Node)
      ) {
        setDatePickerBirthDate(false)
      }

      if (
        datePickerRefLegitimationValidTo.current &&
        !datePickerRefLegitimationValidTo.current.contains(event.target as Node)
      ) {
        setDatePickerLegitimationValidTo(false)
      }
      if (
        datePickerRefLegitimationIssuedOn.current &&
        !datePickerRefLegitimationIssuedOn.current.contains(event.target as Node)
      ) {
        setDatePickerLegitimationIssuedOn(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (pdf && !pdfGeneratedRef.current) {
      pdfGeneratedRef.current = true
      generatePDF()
    }
  }, [pdf, additionalForms])

  const generatePDF = () => {
    const allOpenSchemas: any = {}
    additionalForms.forEach(schema => {
      if (schema?.include && schema.stammdatenType === 'person') {
        allOpenSchemas[schema._id] = true
      }
    })
    setOpenSchemas(allOpenSchemas)
    setIsGeneratingPDF(true)
    expandAllSections()

    setTimeout(() => {
      toPDF()
      setIsGeneratingPDF(false)
    }, 2000)
  }

  const generateSingleFormPDF = async (schemaId: string, schemaName: string) => {
    setOpenSchemas(prev => ({
      ...prev,
      [schemaId]: true,
    }))

    setTimeout(async () => {
      const formElement = document.getElementById(`schema-${schemaId}-content`)
      if (formElement) {
        const html2canvas = (await import('html2canvas')).default
        const jsPDF = (await import('jspdf')).default

        const canvas = await html2canvas(formElement, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
        })

        const imgData = canvas.toDataURL('image/png')
        const imgWidth = 208 // A4 width in mm with margins
        const pageHeight = 285 // A4 height in mm with margins (reduced for header)
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        })

        // Helper function to add header to each page
        const addHeader = () => {
          pdf.setFontSize(10)
          pdf.setTextColor(100)
          pdf.text(`Client: ${personData.clientNumber || 'N/A'}`, 10, 8)
          const clientName =
            personData.first_name && personData.name
              ? `${personData.first_name} ${personData.name}`
              : personData.name || 'N/A'
          pdf.text(`Name: ${clientName}`, 10, 14)
          pdf.setTextColor(0)
        }

        let heightLeft = imgHeight
        let position = 20 // Start below header

        // Add first page with header
        addHeader()
        pdf.addImage(imgData, 'PNG', 1, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        // Add remaining pages
        while (heightLeft > 0) {
          position = heightLeft - imgHeight + 20 // Account for header
          pdf.addPage()
          addHeader()
          pdf.addImage(imgData, 'PNG', 1, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }

        pdf.save(`${schemaName}.pdf`)
      }
    }, 1000)
  }

  const fetchAllData = async () => {
    if (!clientId) return

    fetchCountries()
    fetchBankCountries()

    if (clientId === 'new') {
      const [anredeOpts, addForms, adelstitel, namePref] = await Promise.all([
        getAnrede(),
        apiRequest(`${process.env.REACT_APP_API_URL}/additional/all`, 'GET'),
        getAdelstitel(),
        getNamePrefix(),
      ])
      setAnredeOptions(anredeOpts)
      setAdditionalForms(addForms.docs || [])
      setNobleTitel(adelstitel)
      setNamePrefix(namePref)
      return
    }

    let alive = true
    try {
      const [anredeOpts, clientResp, addForms, adelstitel, namePref, fedState] = await Promise.all([
        getAnrede(),
        fetchClientById(clientId),
        apiRequest(`${process.env.REACT_APP_API_URL}/additional/all`, 'GET'),
        getAdelstitel(),
        getNamePrefix(),
        getFederalState(),
      ])

      if (!alive) return

      setAnredeOptions(anredeOpts)
      setAdditionalForms(addForms.docs || [])
      setNobleTitel(adelstitel)
      setNamePrefix(namePref)

      const root = extractStammdatenRoot(clientResp) ?? []
      const SIMPLE_FIELDS = [
        'clientNumber',
        'client_since',
        'client_to',
        'anrede',
        'academic_degree',
        'first_name',
        'name',
        'short_name',
        'noble_title',
        'name_prefix',
        'alternative_search_name',
        'client_note',
        'nationality',
        'marital_status',
        'federal_state',
        'legitimation_type',
      ]

      let next = JSON.parse(JSON.stringify(personData)) as PersonForm
      for (const p of SIMPLE_FIELDS) {
        const v = getDeep(root, p)
        if (v !== undefined) next = updateNestedField(next, p, v)
      }

      const npl = getDeep(root, 'natural_person_legitimation') || {}
      const nationalityArray = toArray(npl?.nationality ?? []).map((x: any) => ({
        value: typeof x === 'string' ? x : (x?.value ?? ''),
      }))

      next.natural_person_legitimation = {
        ...emptyNaturalPersonLegitimation(),
        ...npl,
        nationality: nationalityArray.length > 0 ? nationalityArray : [{ value: '' }],
      }

      const nl = getDeep(root, 'national_law') || {}
      next.national_law = { ...next.national_law, ...nl }

      const [
        addresses,
        communication,
        banks,
        beziehungs,
        naturalPersonData,
        naturalPersonLegitimation,
        dynamicAdditional,
        files,
        filesClient,
      ] = await Promise.all([
        Promise.resolve(normalizeAddresses(clientResp)),
        Promise.resolve(normalizeCommunication(clientResp, true)),
        Promise.resolve(normalizeBanks(clientResp)),
        Promise.resolve(normalizeBeziehung(clientResp)),
        Promise.resolve(normalizeNaturalPersonData(clientResp, true)),
        Promise.resolve(normalizeNaturalPersonLegitimation(clientResp, true)),
        Promise.resolve(extractDynamicAdditional(clientResp)),
        Promise.resolve(normalizeFiles(clientResp, 'files')),
        Promise.resolve(normalizeFiles(clientResp, 'filesClient')),
      ])

      if (alive) {
        setPersonData({
          ...next,
          address: addresses.length > 0 ? addresses : [emptyAddress()],
          communication,
          bank: banks.length > 0 ? banks : [emptyBank()],
          beziehung: beziehungs.length > 0 ? beziehungs : [emptyBeziehung()],
          natural_person_data: naturalPersonData,
          natural_person_legitimation: naturalPersonLegitimation,
          additional: {
            dynamic: dynamicAdditional,
          },
          files: files,
          filesClient: filesClient,
        })
      }
    } catch (err) {
      console.error('Error fetching client data: ', err)
    }
  }

  const getAccessForPinCode = async () => {
    const result = await fetchAccess(localStorage.getItem('email'))

    if (!!result.accessToken) {
      if (clientId) {
        localStorage.setItem('clientId', clientId)
      }

      setAccessToken(result.accessToken)
      setPin(result.pinCode)
      setPinRequired(result.pinRequired !== false)
      isClientSignRef.current = false
      setAccessForSign(true)
    }
  }

  const requestSignAccess = (documentName: string) => {
    setPendingSignDocument(documentName)
    getAccessForPinCode()
  }

  const checkPin = async (pinCode: number) => {
    try {
      const result = await validatePinCodeClient(pinCode, clientEmail)

      if (result?.accessToken) {
        setAccessToken(result.accessToken)
        setAccess(false)
      }
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const checkPinForSign = async (pinCode: number) => {
    try {
      const result = isClientSignRef.current
        ? await validatePinCodeClient(pinCode, clientEmail)
        : await validatePinCode(pinCode)

      if (result?.accessToken || result?.userAccess) {
        if (result.accessToken) setAccessToken(result.accessToken)
        if (!isClientSignRef.current) setUserAccess(result.userAccess)
        setAccessForSign(false)
        isClientSignRef.current = false

        if (pendingSignDocument) {
          setCurrentDocument(pendingSignDocument)
          setPendingSignDocument(null)
          setSignModal(true)
        }
      }
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const debouncedSave = useDebouncedCallback((field: string, value: any) => {
    saveFieldNow(field, value)
  }, 800)

  const handleChangeLocal = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { id, type, name } = e.target
      const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value

      if (id.includes('additional')) {
        const [schemaName, fieldName, optName] = name.split('.')
        if (type === 'checkbox') {
          setPersonData(prev =>
            updateNestedField(
              prev,
              `additional.dynamic.${schemaName}.${fieldName}.${optName}`,
              value
            )
          )
          debouncedSave(`additional.dynamic.${schemaName}.${fieldName}.${optName}`, value)
        } else {
          setPersonData(prev =>
            updateNestedField(prev, `additional.dynamic.${schemaName}.${fieldName}`, value)
          )
          debouncedSave(`additional.dynamic.${schemaName}.${fieldName}`, value)
        }
      } else {
        setPersonData(prev => updateNestedField(prev, id, value))
        debouncedSave(id, value)

        if (id.includes('bank.') && id.endsWith('.country')) {
          const bankIndex = id.split('.')[1]
          setPersonData(prev => updateNestedField(prev, `bank.${bankIndex}.bank_name`, ''))

          if (value && typeof value === 'string') {
            getBanksByCountry(value)
              .then(banks => setAvailableBanks(banks))
              .catch(() => setAvailableBanks([]))
          } else {
            setAvailableBanks([])
          }
        }
      }
    },
    []
  )

  const handleDateInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = e.currentTarget
      const isValid = value === '' || v.date(value)

      e.currentTarget.setCustomValidity(isValid ? '' : t('invalidDate'))

      setPersonData(prev => updateNestedField(prev, id, value))

      if (isValid) {
        debouncedSave(id, value)
      }
    },
    [debouncedSave]
  )

  const handleDateChange = async (id: string, date: Date | null) => {
    const asString = formatDDMMYYYY(date)

    const input = document.getElementById(id) as HTMLInputElement | null
    input?.setCustomValidity('')

    setPersonData(prev => updateNestedField(prev, id, asString))

    if (!clientId) return alert('Missing clientId')
    debouncedSave(id, asString)
  }

  const saveFieldNow = async (id: string, value?: unknown) => {
    if (!canEdit) return

    let effectiveClientId = clientId === 'new' ? realClientIdRef.current : clientId

    if (clientId === 'new' && !realClientIdRef.current) {
      if (creatingClientRef.current) return
      creatingClientRef.current = true
      try {
        const client = await createClient({ isPerson: 1, isStammdaten: true })
        realClientIdRef.current = client.clientId
        effectiveClientId = client.clientId
        navigate(`/client/stammdaten/person/${client.clientId}`, { replace: true })
      } catch (e) {
        console.error('Error creating client', e)
        creatingClientRef.current = false
        return
      }
      creatingClientRef.current = false
    }

    if (!effectiveClientId) return

    try {
      const fieldValue = value === undefined ? getDeep(personData, id) : value
      let stammdatenPatch = {}
      if (id === 'files' || id === 'filesClient') {
        stammdatenPatch = updateNestedField({}, id, value)
      } else {
        createFieldUpdate(stammdatenPatch, id, fieldValue)
      }

      await updateStammdatenClient({
        clientId: effectiveClientId,
        isStammdaten: true,
        userEmail: localStorage.getItem('email') || '',
        userName: getUserAccess() ? getUserAccess().name : '',
        stammdaten: stammdatenPatch,
      })
    } catch (e) {
      console.error('Save error', e)
    }
  }

  async function history(
    fieldPath: string,
    label: string,
    options?: { value: any; label: string }[]
  ) {
    if (!clientId) {
      alert('Missing clientId')
      return
    }

    apiRequest(
      `${process.env.REACT_APP_API_URL}/clients/${clientId}/stammdaten/history?field=${encodeURIComponent(fieldPath)}&order=desc`,
      'GET'
    ).then(res => {
      setHistoryModal({
        entries: res.history || [],
        title: label,
        isOpen: true,
        options,
      })
    })
  }

  const fetchCountries = async () => {
    try {
      const response = await getCountries()
      setCountries(response.countries.map((c: any) => ({ value: c.code, label: c.name })))
    } catch (err) {
      console.error('Fetch countries: ', err)
    }
  }

  const fetchBankCountries = async () => {
    try {
      const response = await getBankCountries()
      console.log('Fetched bank countries', response)
      setBankCountries(response.map((c: any) => ({ value: c.code, label: c.name })))
    } catch (err) {
      console.error('Fetch bank countries: ', err)
    }
  }

  function ensureOne(arr?: { value: string }[]): { value: string }[] {
    return arr && arr.length > 0 ? arr : [{ value: '' }]
  }

  const addNationalityAt = (
    basePath: 'natural_person_data.nationality' | 'natural_person_legitimation.nationality'
  ) => {
    setPersonData(prev => {
      const arr = getDeep(prev, basePath) || []
      const next = [...arr, { value: '' }]
      return updateNestedField(prev, basePath, next)
    })
  }

  const removeNationalityAt = (
    basePath: 'natural_person_data.nationality' | 'natural_person_legitimation.nationality',
    idx: number
  ) => {
    debouncedSave.cancel()
    setPersonData(prev => {
      const arr = ensureOne(getDeep(prev, basePath))
      const next = arr.filter((_, i) => i !== idx)
      const updated = next.length ? next : [{ value: '' }]
      saveFieldNow(basePath, updated)
      return updateNestedField(prev, basePath, updated)
    })
  }

  const normalizeNaturalPersonData = (resp: any, ensureOneRow = false): NaturalPersonData => {
    const root = extractStammdatenRoot(resp) || {}
    const c = root.natural_person_data ?? {}

    const mapNationality = (x: any) => ({ value: x?.value ?? '' })
    let nationality = toArray(c.nationality ?? []).map(mapNationality)
    if (ensureOneRow && nationality.length === 0) nationality = [makeNationality()]

    return {
      birth_date: c.birth_date ?? '',
      birth_name: c.birth_name ?? '',
      full_first_names: c.full_first_names ?? '',
      place_of_birth: c.place_of_birth ?? '',
      country_of_birth: c.country_of_birth ?? '',
      nationality,
    }
  }

  const normalizeNaturalPersonLegitimation = (
    resp: any,
    ensureOneRow = false
  ): NaturalPersonLegitimation => {
    const root = extractStammdatenRoot(resp) || {}
    const c = root.natural_person_legitimation ?? {}

    const mapNationality = (x: any) => ({ value: x?.value ?? '' })
    let nationality = toArray(c.nationality ?? []).map(mapNationality)
    if (ensureOneRow && nationality.length === 0) nationality = [makeNationality()]

    return {
      comment: c.comment ?? '',
      pep: Boolean(c.pep),
      id_number: c.id_number ?? '',
      birth_date: c.birth_date ?? '',
      nationality,
      place_of_birth: c.place_of_birth ?? '',
      valid_to: c.valid_to ?? '',
      issued_on: c.issued_on ?? '',
      issuing_authority: c.issuing_authority ?? '',
    }
  }

  const handleFileDownload = async (filename: string, type: string) => {
    await downloadKycFile(clientId || '', personData.name || '', type, filename)
  }

  const handleFileView = (filename: string, type: string) => {
    setPreviewTarget({ clientId: clientId || '', firmename: personData.name || '', type, filename })
  }

  const handleUpload = useCallback(
    async (type: string, fieldPath?: string) => {
      if (!selectedFile) return alert(t('chooseFile'))

      try {
        const entry = await uploadStammdatenFile({
          file: selectedFile,
          clientId: clientId || '',
          type,
          name: personData.name,
          email: isSharedClient ? clientEmail : undefined,
        })

        if (type === 'files') {
          setPersonData(prev => {
            const newFiles = [...(prev.files || []), entry]
            const next = { ...prev, files: newFiles }

            saveFieldNow('files', newFiles)

            return next
          })
        } else if (type === 'filesClient') {
          setPersonData(prev => {
            const newFiles = [...(prev.filesClient || []), entry]
            const next = { ...prev, filesClient: newFiles }

            saveFieldNow('filesClient', newFiles)

            return next
          })
        } else if (type === 'additionalFormFiles' && fieldPath) {
          setPersonData(prev => {
            const currentFiles = toArray(getDeep(prev, fieldPath))
            const newFiles = [...currentFiles, entry]
            const next = updateNestedField(prev, fieldPath, newFiles)

            saveFieldNow(fieldPath, newFiles)

            return next
          })
        }

        alert('Uploaded success')
        setSelectedFile(null)
      } catch (err) {
        console.error(err)
        alert('Upload error')
      }
    },
    [clientId, selectedFile, isSharedClient, clientEmail, personData.files]
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSelectedFile(e.target.files[0])
    }
  }

  const toggleSchema = (id: string) => setOpenSchemas(prev => ({ ...prev, [id]: !prev[id] }))

  const makeNationality = () => ({
    value: '',
  })

  const handleAddAddress = () =>
    setPersonData(prev => {
      const updatedAddress = [...prev.address, emptyAddress()]
      saveFieldNow('address', updatedAddress)
      return { ...prev, address: updatedAddress }
    })

  const handleRemoveAddress = (indexToRemove: number) =>
    setPersonData(prev => {
      const updatedAddress = prev.address.filter((_, index) => index !== indexToRemove)
      saveFieldNow('address', updatedAddress)
      return { ...prev, address: updatedAddress }
    })

  const handleAddBank = () =>
    setPersonData(prev => {
      const updatedBank = [...prev.bank, emptyBank()]
      saveFieldNow('bank', updatedBank)
      return { ...prev, bank: updatedBank }
    })

  const handleRemoveBank = (indexToRemove: number) =>
    setPersonData(prev => {
      const updatedBank = prev.bank.filter((_, index) => index !== indexToRemove)
      saveFieldNow('bank', updatedBank)
      return { ...prev, bank: updatedBank }
    })

  const handleAddBeziehungen = () =>
    setPersonData(prev => {
      const updatedBeziehung = [...prev.beziehung, emptyBeziehung()]
      saveFieldNow('beziehung', updatedBeziehung)
      return { ...prev, beziehung: updatedBeziehung }
    })

  const handleRemoveBeziehung = (indexToRemove: number) =>
    setPersonData(prev => {
      const updatedBeziehung = prev.beziehung.filter((_, index) => index !== indexToRemove)
      saveFieldNow('beziehung', updatedBeziehung)
      return { ...prev, beziehung: updatedBeziehung }
    })

  const handleToggleDifferent = async (idx: number, checked: boolean) => {
    setPersonData(prev => {
      let next = updateNestedField(prev, `address.${idx}.use_different_delivery_addressee`, checked)
      if (!checked) {
        next = updateNestedField(next, `address.${idx}.abbrev_salutation`, '')
        next = updateNestedField(next, `address.${idx}.different_person`, '')
        next = updateNestedField(next, `address.${idx}.delivery_addressee`, '')
      }
      return next
    })

    if (!clientId) return

    debouncedSave(`address.${idx}.use_different_delivery_addressee`, checked)
  }

  const handleBeziehungenTypeChange = async (idx: number, value: string) => {
    setPersonData(prev => {
      let next = updateNestedField(prev, `beziehung.${idx}.addressee_type`, value)
      debouncedSave(`beziehung.${idx}.addressee_type`, value)
      return next
    })
  }

  const handleAgreementChange = async (value: boolean) => {
    setValidationModal({
      agreed: value,
      isOpen: validationModal.isOpen,
      field: validationModal.field,
    })

    if (value) {
      handleSubmit()
    }
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    expandAllSections()

    const allOpenSchemas: any = {}
    additionalForms.forEach(schema => {
      if (schema?.include && schema.stammdatenType === 'person') {
        allOpenSchemas[schema._id] = true
      }
    })
    setOpenSchemas(allOpenSchemas)

    setTimeout(() => {
      if (!formRef.current) {
        console.error('Form element not found')
        return
      }

      if (!formRef.current.checkValidity()) {
        setValidationModal({
          isOpen: validationModal.isOpen,
          field: validationModal.field,
          agreed: false,
        })
        formRef.current.reportValidity()

        const invalidDateInput = formRef.current.querySelector<HTMLInputElement>('input:invalid')

        if (invalidDateInput?.validity.customError) {
          setValidationModal({
            isOpen: true,
            field: invalidDateInput.validationMessage,
            agreed: validationModal.agreed,
          })
          return
        }

        const form = formRef.current as HTMLFormElement
        if (!form.checkValidity()) {
          const firstInvalid = form.querySelector<HTMLElement>(':invalid')
          if (firstInvalid) {
            const input = firstInvalid as HTMLInputElement

            setValidationModal({
              isOpen: true,
              field: validationModal.field,
              agreed: validationModal.agreed,
            })
            setValidationModal({
              isOpen: true,
              field: input.name || '',
              agreed: validationModal.agreed,
            })
          }
        }

        return
      }

      if (isUserLoggedIn()) {
        navigate('/client/stammdaten')
      } else {
        navigate('/user/login')
      }
    }, 2000)
  }
  const handleNationalityXClick = (idx: number) => {
    debouncedSave.cancel()
    setPersonData(prev => {
      const arr = [...prev.natural_person_data.nationality]

      if (idx === 0) {
        // clear value prvog polja
        arr[0] = { value: '' }
      } else {
        // ukloni dodatno polje
        arr.splice(idx, 1)
      }

      const updated = arr.length ? arr : [{ value: '' }]
      saveFieldNow('natural_person_data.nationality', updated)
      return {
        ...prev,
        natural_person_data: {
          ...prev.natural_person_data,
          nationality: updated,
        },
      }
    })
  }

  const isFutureDate = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const compare = new Date(date)
    compare.setHours(0, 0, 0, 0)

    return compare > today
  }

  const handleClose = () => {
    setSignModal(false)
  }

  const isAustria = (getDeep(personData, 'national_law.nationality') || '') === 'AT'

  return (
    <>
      <Container className='m-auto'>
        <Row className='mb-5'>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            {!isSharedClient && <KLayerNavigation logged={isUserLoggedIn()} />}
          </div>
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4 mb-8'
          >
            <fieldset disabled={!canEdit} className='border-0 p-0 m-0' style={{ minWidth: 0 }}>
              {!canEdit && (
                <div className='alert alert-info mt-3 ms-3'>{t('readOnlyNoPermission')}</div>
              )}
              <div ref={targetRef}>
                <Row className='text-muted ms-3'>
                  {t('client')}: {personData.clientNumber}
                  <br />
                  {t('name')}: {personData.first_name} {personData.name}
                </Row>

                <Card className='border-0 mb-5'>
                  <Card.Body>
                    <Col className='col-sm-7 mt-4'>
                      <Card.Title>
                        <h4 className='fw-bold'>
                          {t('KYC - Know Your Customer ')}
                          <small className='text-muted'>{t('stammdaten')}</small>
                        </h4>
                      </Card.Title>
                    </Col>

                    <hr />

                    {/* Mandant seit/bis */}
                    <Row className='mt-4'>
                      <Col className='col-sm-2'>
                        <label
                          className={`form-control-label ${requiredFields.client_since ? 'required' : ''}`}
                        >
                          {t('mandantNumber')}
                        </label>
                      </Col>

                      <Col>
                        <div className='form-group'>
                          <FormControl
                            className='w-25 d-inline'
                            type='text'
                            id='clientNumber'
                            value={personData.clientNumber}
                            onChange={handleChangeLocal}
                          />
                        </div>
                      </Col>
                    </Row>

                    <hr />

                    {/* Mandant seit/bis */}
                    <Row className='mt-4'>
                      <Col className='col-sm-2'>
                        <label
                          className={`form-control-label ${requiredFields.client_since ? 'required' : ''}`}
                        >
                          {t('client')}
                        </label>
                      </Col>
                      <Col>
                        {/* Labels + history */}
                        <Row className='mb-1'>
                          <Col className='col-sm-2 d-flex align-items-center gap-2'>
                            <span>{t('since')}</span>
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0'
                              onClick={() => history('stammdaten.client_since', 'Client since')}
                            >
                              <i className='fa fa-history text-dark' />
                            </button>
                          </Col>

                          <Col className='col-sm-2 d-flex align-items-center gap-2'>
                            <span>{t('until')}</span>
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0'
                              onClick={() => history('stammdaten.client_to', 'Client until')}
                            >
                              <i className='fa fa-history text-dark' />
                            </button>
                          </Col>
                        </Row>

                        {/* Inputs */}
                        <Row>
                          <Col className='col-sm-2'>
                            <Form.Control
                              type='text'
                              id='client_since'
                              name='seit'
                              className='w-100'
                              value={personData.client_since || ''}
                              onClick={() => setDatePickerClientSince(!datePickerClientSince)}
                              onChange={handleDateInputChange}
                              placeholder='DD.MM.YYYY'
                              inputMode='numeric'
                              maxLength={10}
                              required={requiredFields.client_since}
                            />

                            <KDayPicker
                              ref={datePickerRefClientSince}
                              open={datePickerClientSince}
                              value={personData.client_since}
                              required={requiredFields.client_since}
                              disabled={{ after: new Date() }}
                              onSelect={d => {
                                handleDateChange('client_since', d)
                                setDatePickerClientSince(false)
                              }}
                            />
                          </Col>

                          <Col className='col-sm-2'>
                            <Form.Control
                              type='text'
                              id='client_to'
                              name='bis'
                              className='w-100'
                              value={personData.client_to || ''}
                              onClick={() => setDatePickerClientTo(!datePickerClientTo)}
                              onChange={handleDateInputChange}
                              placeholder='DD.MM.YYYY'
                              inputMode='numeric'
                              maxLength={10}
                              required={requiredFields.client_to}
                            />

                            <KDayPicker
                              ref={datePickerRefClientTo}
                              open={datePickerClientTo}
                              value={personData.client_to}
                              required={requiredFields.client_to}
                              disabled={{ after: new Date() }}
                              onSelect={d => {
                                handleDateChange('client_to', d)
                                setDatePickerClientTo(false)
                              }}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Angaben zur Natürlichen Person */}
                    <h6 className=' p-2 mb-3 mt-3'>
                      <FilePersonFill />
                      &nbsp;{t('angabenNaturlichenPerson')}
                    </h6>

                    {[
                      {
                        id: 'anrede',
                        name: t('anrede'),
                        label: t('anrede'),
                        isSelect: true,
                        required: requiredFields.anrede,
                      },
                      {
                        id: 'academic_degree',
                        name: t('titelGrad'),
                        label: t('titelGrad'),
                        required: requiredFields.academic_degree,
                      },
                      { id: 'first_name', name: t('vorname'), label: t('vorname'), required: true },
                      { id: 'name', name: t('nachname'), label: t('nachname'), required: true },
                      {
                        id: 'short_name',
                        name: t('nameKurz'),
                        label: t('nameKurz'),
                        required: requiredFields.short_name,
                      },
                      {
                        id: 'noble_title',
                        name: t('adelstitel'),
                        label: t('adelstitel'),
                        isSelect: true,
                        required: requiredFields.noble_title,
                      },
                      {
                        id: 'name_prefix',
                        name: t('namensvorsatz'),
                        label: t('namensvorsatz'),
                        isSelect: true,
                        required: requiredFields.name_prefix,
                      },
                      {
                        id: 'alternative_search_name',
                        name: t('alternativerSuchname'),
                        label: t('alternativerSuchname'),
                        required: requiredFields.alternative_search_name,
                      },
                    ].map((f, idx) => (
                      <Row className='mt-3' key={idx}>
                        <Col className='col-sm-2'>
                          <label className={`form-control-label ${f.required ? 'required' : ''}`}>
                            {f.label}
                          </label>
                        </Col>
                        <Col>
                          <div className='form-group'>
                            {f.isSelect ? (
                              <FormSelect
                                name={f.name}
                                className='w-25 d-inline'
                                id={f.id}
                                value={getDeep(personData, f.id) || ''}
                                onChange={handleChangeLocal}
                                required={f.required}
                              >
                                <option value=''>{'-- '}</option>
                                {f.id === 'anrede' &&
                                  anredeOptions.map((opt: any, idx: number) => (
                                    <option key={idx} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                {f.id === 'noble_title' &&
                                  nobleTitle.map((opt: any, idx: number) => (
                                    <option key={idx} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                {f.id === 'name_prefix' &&
                                  namePrefix.map((opt: any, idx: number) => (
                                    <option key={idx} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}

                                {/* Opcije dolaze iz backend-a (anrede/noble_title/name_prefix) */}
                              </FormSelect>
                            ) : (
                              <FormControl
                                className='w-25 d-inline'
                                type='text'
                                id={f.id}
                                value={getDeep(personData, f.id) || ''}
                                onChange={handleChangeLocal}
                                required={f.required}
                                name={f.name}
                              />
                            )}
                            <button
                              type='button'
                              className='btn btn-link btn-sm'
                              onClick={() => history(`stammdaten.${f.id}`, f.label)}
                            >
                              <i className='fa fa-history text-dark' aria-hidden='true'></i>
                            </button>
                          </div>
                        </Col>
                      </Row>
                    ))}

                    {/* Notiz */}
                    <Row className='mt-4'>
                      <Col className='col-sm-2'>
                        <label
                          className={`form-control-label text-capitalize ${requiredFields.note ? 'required' : ''}`}
                        >
                          {t('notes')}
                        </label>
                      </Col>
                      <Col>
                        <div className='form-group'>
                          <textarea
                            className='w-50 d-inline'
                            id='client_note'
                            value={personData.client_note}
                            onChange={handleChangeLocal}
                            required={requiredFields.note}
                            name={t('notes')}
                          />
                          <button
                            type='button'
                            className='btn btn-link btn-sm'
                            onClick={() => history('stammdaten.client_note', t('notes'))}
                          >
                            <i className='fa fa-history text-dark' aria-hidden='true'></i>
                          </button>
                        </div>
                      </Col>
                    </Row>

                    {/* ===================== Persönliche Daten ===================== */}
                    <Row className='mt-4'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.PERSONAL)}
                      >
                        <PersonFill />
                        &nbsp;{t('persönlicheDaten')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.PERSONAL] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>

                    {expandedSections[SECTION_KEYS.PERSONAL] && (
                      <>
                        {/* Geburtsdatum (date) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_data.birth_date ? 'required' : ''}`}
                            >
                              {t('dateOfBirth')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <Form.Control
                                type='text'
                                id='natural_person_data.birth_date'
                                name={t('dateOfBirth')}
                                className='w-25 d-inline'
                                value={personData.natural_person_data.birth_date || ''}
                                onClick={() => setDatePickerBirthDate(!datePickerBirthDate)}
                                onChange={handleDateInputChange}
                                placeholder='DD.MM.YYYY'
                                inputMode='numeric'
                                maxLength={10}
                                required={requiredFields.natural_person_data.birth_date}
                              />
                              <KDayPicker
                                ref={datePickerRefBirthDate}
                                open={datePickerBirthDate}
                                value={personData.natural_person_data.birth_date}
                                required={requiredFields.natural_person_data.birth_date}
                                disabled={{ after: new Date() }}
                                onSelect={d => {
                                  handleDateChange('natural_person_data.birth_date', d)
                                  setDatePickerBirthDate(false)
                                }}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_data.birth_date',
                                    t('dateOfBirth')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Geburtsname (text) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_data.birth_name ? 'required' : ''}`}
                            >
                              {t('geburtsname')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <FormControl
                                name={t('geburtsname')}
                                className='w-25 d-inline'
                                type='text'
                                id='natural_person_data.birth_name'
                                value={getDeep(personData, 'natural_person_data.birth_name') || ''}
                                required={requiredFields.natural_person_data.birth_name}
                                onChange={handleChangeLocal}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_data.birth_name',
                                    t('geburtsname')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Vollständige Vornamen (text) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-3'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_data.full_first_names ? 'required' : ''}`}
                            >
                              {t('vollständigeVornamen')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <FormControl
                                name={t('vollständigeVornamen')}
                                className='w-25 d-inline'
                                type='text'
                                id='natural_person_data.full_first_names'
                                value={
                                  getDeep(personData, 'natural_person_data.full_first_names') || ''
                                }
                                required={requiredFields.natural_person_data.full_first_names}
                                onChange={handleChangeLocal}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_data.full_first_names',
                                    t('vollständigeVornamen')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Geburtsort (text) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_data.place_of_birth ? 'required' : ''}`}
                            >
                              {t('geburtsort')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <FormControl
                                name={t('geburtsort')}
                                className='w-25 d-inline'
                                type='text'
                                id='natural_person_data.place_of_birth'
                                value={
                                  getDeep(personData, 'natural_person_data.place_of_birth') || ''
                                }
                                required={requiredFields.natural_person_data.place_of_birth}
                                onChange={handleChangeLocal}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_data.place_of_birth',
                                    t('geburtsort')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Geburtsland (text) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_data.country_of_birth ? 'required' : ''}`}
                            >
                              {t('geburtsland')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <FormSelect
                                name={t('landFirmensitzes')}
                                className='w-25 d-inline'
                                id='natural_person_data.country_of_birth'
                                value={
                                  getDeep(personData, 'natural_person_data.country_of_birth') || ''
                                }
                                required={requiredFields.natural_person_data.country_of_birth}
                                onChange={handleChangeLocal}
                              >
                                <option value=''>--</option>
                                {countries.map((option: any) => (
                                  <option key={option.value} value={option.value}>
                                    {t(option.label)}
                                  </option>
                                ))}
                              </FormSelect>
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_data.country_of_birth',
                                    t('geburtsland')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Persönliche Daten → Staatsangehörigkeit (više vrednosti) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_data.nationality ? 'required' : ''}`}
                            >
                              {t('staatsangehörigkeit')}
                            </label>
                          </Col>
                          <Col>
                            {personData.natural_person_data.nationality.map((val, idx) => {
                              return (
                                <div key={idx} className='d-flex align-items-center mb-2'>
                                  <FormSelect
                                    name={t('staatsangehörigkeit')}
                                    className='w-25 d-inline'
                                    id={`natural_person_data.nationality.${idx}.value`}
                                    value={val.value}
                                    required={requiredFields.natural_person_data.nationality}
                                    onChange={handleChangeLocal}
                                  >
                                    <option value=''>{'--'}</option>
                                    {nationalityOptions.map((opt: any) => (
                                      <option key={opt.value} value={opt.value}>
                                        {t(opt.label)}
                                      </option>
                                    ))}
                                  </FormSelect>

                                  <Button
                                    variant='light'
                                    size='sm'
                                    className='ms-2 text-danger border'
                                    onClick={() => handleNationalityXClick(idx)}
                                    title={idx === 0 ? t('clear') : t('remove')}
                                  >
                                    ×
                                  </Button>
                                </div>
                              )
                            })}

                            <Button
                              variant='success'
                              size='sm'
                              onClick={() => addNationalityAt('natural_person_data.nationality')}
                              title={t('hinzufügen') as string}
                              className='btn btn-light p-0 d-flex align-items-center justify-content-center'
                              style={{
                                width: '28px',
                                height: '28px',
                                border: '1px solid #ced4da',
                                color: '#198754', // bootstrap green
                                fontWeight: 600,
                                lineHeight: 1,
                              }}
                            >
                              +
                            </Button>
                          </Col>
                        </Row>
                        <hr />

                        {/* Social Security Number (text) — samo ako JE Austrija */}
                        {isAustria && (
                          <Row className='mt-3'>
                            <Col className='col-sm-2'>
                              <label className='form-control-label'>{t('sozialversNr')}</label>
                            </Col>
                            <Col>
                              <div className='form-group'>
                                <FormControl
                                  name={t('sozialversNr')}
                                  className='w-25 d-inline'
                                  type='text'
                                  id='national_law.social_security_number'
                                  value={
                                    getDeep(personData, 'national_law.social_security_number') || ''
                                  }
                                  onChange={handleChangeLocal}
                                />
                                <button
                                  type='button'
                                  className='btn btn-link btn-sm'
                                  onClick={() =>
                                    history(
                                      'stammdaten.national_law.social_security_number',
                                      t('sozialversicherungsnummer')
                                    )
                                  }
                                >
                                  <i className='fa fa-history text-dark' aria-hidden='true'></i>
                                </button>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    {/* ===================== Legitimation/Identifizierung ===================== */}
                    <Row className='mt-4'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.LEGIT)}
                      >
                        <PersonFill />
                        &nbsp;{t('Legitimation/Identifizierung')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.LEGIT] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>

                    {expandedSections[SECTION_KEYS.LEGIT] && (
                      <>
                        {/* Legitimationsart */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_legitimation.legitimation_type ? 'required' : ''}`}
                            >
                              {t('legitimationsart')}
                            </label>
                          </Col>
                          <Col>
                            <FormSelect
                              name={t('legitimationsart')}
                              className='w-25 d-inline'
                              id='legitimation_type'
                              value={personData.legitimation_type}
                              onChange={handleChangeLocal}
                              required={
                                requiredFields.natural_person_legitimation.legitimation_type
                              }
                            >
                              <option value=''>{'-- '}</option>
                              <option key='PA' value='PA'>
                                {t('personalausweis')}
                              </option>
                              <option key='RP' value='RP'>
                                {t('reisepass')}
                              </option>
                              <option key='FS' value='FS'>
                                {t('führerschein')}
                              </option>
                              <option key='SA' value='SA'>
                                {t('sonstigerAusweis')}
                              </option>
                              <option key='UK' value='UK'>
                                {t('urkunde')}
                              </option>
                              <option key='PB' value='PB'>
                                {t('persönlicheBekanntschaft')}
                              </option>
                              <option key='EZ' value='EZ'>
                                {t('erkennungszeuge')}
                              </option>
                              <option key='SK' value='SK'>
                                {t('besondereSachkunde')}
                              </option>
                              <option key='IN' value='IN'>
                                {t('elektronischerIdentitätsnachweis')}
                              </option>
                              <option key='ES' value='ES'>
                                {t('qualifizierteElektronischeSignatur')}
                              </option>
                              <option key='IS' value='IS'>
                                {t('elektronischesIdentifizierungssystem')}
                              </option>
                            </FormSelect>
                            <button
                              type='button'
                              className='btn btn-link btn-sm'
                              onClick={() =>
                                history('stammdaten.legitimation_type', 'Legitimationsart', [
                                  { value: 'PA', label: t('personalausweis') },
                                  { value: 'RP', label: t('reisepass') },
                                  { value: 'FS', label: t('führerschein') },
                                  { value: 'SA', label: t('sonstigerAusweis') },
                                  { value: 'UK', label: t('urkunde') },
                                  { value: 'PB', label: t('persönlicheBekanntschaft') },
                                  { value: 'EZ', label: t('erkennungszeuge') },
                                  { value: 'SK', label: t('besondereSachkunde') },
                                  { value: 'IN', label: t('elektronischerIdentitätsnachweis') },
                                  { value: 'ES', label: t('qualifizierteElektronischeSignatur') },
                                  { value: 'IS', label: t('elektronischesIdentifizierungssystem') },
                                ])
                              }
                            >
                              <i className='fa fa-history text-dark' aria-hidden='true'></i>
                            </button>
                          </Col>
                        </Row>

                        {/* Komentar + PEP */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_legitimation.comment ? 'required' : ''}`}
                            >
                              {t('kommentar')}
                            </label>
                          </Col>
                          <Col>
                            <textarea
                              style={{ backgroundColor: 'rgb(255, 255, 220)' }}
                              name={t('kommentar')}
                              className='w-50 d-inline'
                              id='natural_person_legitimation.comment'
                              value={personData.natural_person_legitimation.comment}
                              onChange={handleChangeLocal}
                              required={requiredFields.natural_person_legitimation.comment}
                            />
                          </Col>
                        </Row>

                        <Row className='mt-3'>
                          <Col className='col-sm-4'>
                            <label className={`form-control-label`}>
                              {t('Politisch exponierte Person (PEP)')}
                            </label>
                          </Col>
                          <Col className='d-flex align-items-center gap-2'>
                            <div
                              style={{
                                backgroundColor: '#FFFFDC',
                                padding: '6px 10px',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <input
                                name={t('Politisch exponierte Person (PEP)')}
                                type='checkbox'
                                id='natural_person_legitimation.pep'
                                checked={personData.natural_person_legitimation.pep}
                                onChange={handleChangeLocal}
                              />
                            </div>

                            <button
                              type='button'
                              className='btn btn-link btn-sm'
                              onClick={() =>
                                history(
                                  'stammdaten.natural_person_legitimation.pep',
                                  t('Politisch exponierte Person (PEP)')
                                )
                              }
                            >
                              <i className='fa fa-history text-dark' />
                            </button>
                          </Col>
                        </Row>

                        <Row className='mt-3'>
                          <Col className='text-muted'>{t('gwgIdentificationHint')}</Col>
                        </Row>

                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_legitimation.id_number ? 'required' : ''}`}
                            >
                              {t('ausweisnummer')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <FormControl
                                name={t('ausweisnummer')}
                                className='w-25 d-inline'
                                type='text'
                                id='natural_person_legitimation.id_number'
                                value={
                                  getDeep(personData, 'natural_person_legitimation.id_number') || ''
                                }
                                onChange={handleChangeLocal}
                                required={requiredFields.natural_person_legitimation.id_number}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_legitimation.id_number',
                                    t('ausweisnummer')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Gültig bis (date) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_legitimation.valid_to ? 'required' : ''}`}
                            >
                              {t('gültigBis')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <Form.Control
                                type='text'
                                id='natural_person_legitimation.valid_to'
                                name={t('gültigBis')}
                                className='w-25 d-inline'
                                value={personData.natural_person_legitimation.valid_to || ''}
                                onClick={() =>
                                  setDatePickerLegitimationValidTo(!datePickerLegitimationValidTo)
                                }
                                onChange={handleDateInputChange}
                                placeholder='DD.MM.YYYY'
                                inputMode='numeric'
                                maxLength={10}
                                required={requiredFields.natural_person_legitimation.valid_to}
                              />
                              <KDayPicker
                                ref={datePickerRefLegitimationValidTo}
                                open={datePickerLegitimationValidTo}
                                value={personData.natural_person_legitimation.valid_to}
                                required={requiredFields.natural_person_legitimation.valid_to}
                                onSelect={d => {
                                  handleDateChange('natural_person_legitimation.valid_to', d)
                                  setDatePickerLegitimationValidTo(false)
                                }}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_legitimation.valid_to',
                                    t('gültigBis')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Ausgestellt am (date) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_legitimation.issued_on ? 'required' : ''}`}
                            >
                              {t('ausgestelltAm')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <Form.Control
                                type='text'
                                id='natural_person_legitimation.issued_on'
                                name={t('ausgestelltAm')}
                                className='w-25 d-inline'
                                value={personData.natural_person_legitimation.issued_on || ''}
                                onClick={() =>
                                  setDatePickerLegitimationIssuedOn(!datePickerLegitimationIssuedOn)
                                }
                                onChange={handleDateInputChange}
                                placeholder='DD.MM.YYYY'
                                inputMode='numeric'
                                maxLength={10}
                                required={requiredFields.natural_person_legitimation.issued_on}
                              />
                              <KDayPicker
                                ref={datePickerRefLegitimationIssuedOn}
                                open={datePickerLegitimationIssuedOn}
                                value={personData.natural_person_legitimation.issued_on}
                                required={requiredFields.natural_person_legitimation.issued_on}
                                disabled={isFutureDate}
                                onSelect={d => {
                                  handleDateChange('natural_person_legitimation.issued_on', d)
                                  setDatePickerLegitimationIssuedOn(false)
                                }}
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_legitimation.issued_on',
                                    t('ausgestelltAm')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>

                        {/* Ausstellende Behörde (text) */}
                        <Row className='mt-3'>
                          <Col className='col-sm-2'>
                            <label
                              className={`form-control-label ${requiredFields.natural_person_legitimation.issuing_authority ? 'required' : ''}`}
                            >
                              {t('ausstellendeBehörde')}
                            </label>
                          </Col>
                          <Col>
                            <div className='form-group'>
                              <FormControl
                                name={t('ausstellendeBehörde')}
                                className='w-25 d-inline'
                                type='text'
                                id='natural_person_legitimation.issuing_authority'
                                value={
                                  getDeep(
                                    personData,
                                    'natural_person_legitimation.issuing_authority'
                                  ) || ''
                                }
                                onChange={handleChangeLocal}
                                required={
                                  requiredFields.natural_person_legitimation.issuing_authority
                                }
                              />
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() =>
                                  history(
                                    'stammdaten.natural_person_legitimation.issuing_authority',
                                    t('ausstellendeBehörde')
                                  )
                                }
                              >
                                <i className='fa fa-history text-dark' aria-hidden='true'></i>
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* ===================== Address ===================== */}
                    <Row className='mt-2'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.ADDRESS)}
                      >
                        <PersonVcardFill />
                        &nbsp;{t('Adressen')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.ADDRESS] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>
                    {expandedSections[SECTION_KEYS.ADDRESS] && (
                      <MemoizedStammdatenAddress
                        form={personData}
                        options={countries}
                        handleChangeLocal={handleChangeLocal}
                        handleDateInputChange={handleDateInputChange}
                        history={history}
                        handleRemoveAddress={handleRemoveAddress}
                        handleDateChange={handleDateChange}
                        handleToggleDifferent={handleToggleDifferent}
                        handleAddAddress={handleAddAddress}
                        required={requiredFields}
                      />
                    )}

                    {/* ===================== Kommunikation ===================== */}
                    <Row className='mt-2'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.COMMUNICATION)}
                      >
                        <Globe2 />
                        &nbsp;{t('Kommunikation')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.COMMUNICATION] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>
                    {expandedSections[SECTION_KEYS.COMMUNICATION] && (
                      <MemoizedStammdatenCommunication
                        form={personData as any}
                        handleChangeLocal={handleChangeLocal}
                        history={history}
                        handleAddRow={(kind: keyof Communication) => {
                          setPersonData(prev => {
                            const next = { ...prev, communication: { ...prev.communication } }
                            const arr = [...(next.communication as any)[kind]]
                            const base: any = { value: '', note: '' }
                            if (kind === 'phone')
                              arr.push({ ...base, is_default: false, is_gl: false })
                            else if (kind === 'email')
                              arr.push({ ...base, is_default: false, is_invoice: false })
                            else if (kind === 'url') arr.push({ ...base, is_default: false })
                            else arr.push(base)
                            ;(next.communication as any)[kind] = arr
                            return next
                          })
                        }}
                        handleRemoveRow={(kind: keyof Communication, indexToRemove: number) => {
                          debouncedSave.cancel()
                          setPersonData(prev => {
                            const updated = (prev.communication as any)[kind].filter(
                              (_: any, i: number) => i !== indexToRemove
                            )
                            const updatedCommunication = { ...prev.communication, [kind]: updated }
                            saveFieldNow('communication', updatedCommunication)
                            return { ...prev, communication: updatedCommunication }
                          })
                        }}
                        required={requiredFields}
                      />
                    )}

                    {/* ===================== Banken ===================== */}
                    <Row className='mt-2'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.BANK)}
                      >
                        <CreditCardFill />
                        &nbsp;{t('Banken')}
                        <b className='ms-auto'>{expandedSections[SECTION_KEYS.BANK] ? '▼' : '►'}</b>
                      </h5>
                    </Row>
                    {expandedSections[SECTION_KEYS.BANK] && (
                      <MemoizedStammdatenBank
                        form={personData}
                        handleChangeLocal={handleChangeLocal}
                        handleDateInputChange={handleDateInputChange}
                        history={history}
                        options={bankCountries}
                        banks={banks}
                        handleDateChange={handleDateChange}
                        handleAddBank={handleAddBank}
                        handleRemoveBank={handleRemoveBank}
                        required={requiredFields}
                        availableBanks={availableBanks}
                      />
                    )}

                    {/* ===================== Beziehungen ===================== */}
                    <Row className='mt-2'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.RELATIONSHIPS)}
                      >
                        <CreditCardFill />
                        &nbsp;{t('Beziehungen')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.RELATIONSHIPS] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>
                    {expandedSections[SECTION_KEYS.RELATIONSHIPS] && (
                      <MemoizedStammdatenBeziehungen
                        form={personData}
                        handleChangeLocal={handleChangeLocal}
                        handleDateInputChange={handleDateInputChange}
                        history={history}
                        options={countries}
                        beziehungs={beziehungs}
                        handleBeziehungenTypeChange={handleBeziehungenTypeChange}
                        handleAddBeziehungen={handleAddBeziehungen}
                        handleRemoveBeziehung={handleRemoveBeziehung}
                        handleDateChange={handleDateChange}
                        required={requiredFields}
                      />
                    )}

                    {/* ===================== Dokumente (Kanzlei) ===================== */}
                    <Row className='mt-4'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.DOCUMENTS_LAWYER)}
                      >
                        <FileEarmarkFill />
                        &nbsp;{t('DokumenteKanzlei')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.DOCUMENTS_LAWYER] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>
                    {expandedSections[SECTION_KEYS.DOCUMENTS_LAWYER] && (
                      <div id='documents-data' className='mb-4'>
                        <Row className='mt-2 border-bottom pb-2'>
                          <Col className='col-sm-3'>
                            <b>{t('users')}</b>
                          </Col>
                          <Col className='col-sm-3'>
                            <b>{t('datumHochladen')}</b>
                          </Col>
                          <Col className='col-sm-3'>
                            <b>{t('file')}</b>
                          </Col>
                          <Col className='col-sm-3'>
                            <b>{t('actions')}</b>
                          </Col>
                        </Row>
                        {personData.files.map((file, idx) => (
                          <Row key={idx} className='mt-2 border-bottom pb-2'>
                            <Col className='col-sm-3'>{file.user}</Col>
                            <Col className='col-sm-3'>{file.date}</Col>
                            <Col className='col-sm-3'>{file.name}</Col>
                            <Col className='col-sm-3'>
                              <Button
                                variant='outline-secondary'
                                size='sm'
                                className='me-1'
                                title={t('preview')}
                                onClick={() => handleFileView(file.name, 'stammdaten_files')}
                              >
                                <i className='bi bi-eye'></i>
                              </Button>
                              <Button
                                variant='outline-primary'
                                size='sm'
                                className='me-1'
                                title={t('download')}
                                onClick={() => handleFileDownload(file.name, 'stammdaten_files')}
                              >
                                <i className='bi bi-download'></i>
                              </Button>
                              {!isSharedClient && (
                                <Button
                                  id={`sign-${idx}`}
                                  variant='outline-secondary'
                                  size='sm'
                                  className='me-1'
                                  title={t('DS|Sign')}
                                  onClick={() => {
                                    setDocumentType('files')
                                    requestSignAccess(file.name)
                                  }}
                                >
                                  <i className='bi bi-pencil-square'></i>
                                </Button>
                              )}
                            </Col>
                          </Row>
                        ))}
                        {!isSharedClient && (
                          <Row className='my-4'>
                            <Col>
                              <input
                                className='do-not-save d-block mb-3'
                                type='file'
                                name='files[]'
                                id='file'
                                multiple
                                onChange={handleFileChange}
                              ></input>
                              <Button
                                variant='success'
                                className='btn-sm pull-right'
                                type='submit'
                                onClick={() => handleUpload('files')}
                              >
                                {t('Hochladen')}
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </div>
                    )}

                    {/* ===================== Dokumente (Mandant) ===================== */}
                    <Row className='mt-4'>
                      <h5
                        className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'
                        onClick={() => toggleSection(SECTION_KEYS.DOCUMENTS_CLIENT)}
                      >
                        <FileEarmarkFill />
                        &nbsp;{t('dokumenteMandantKanzlei')}
                        <b className='ms-auto'>
                          {expandedSections[SECTION_KEYS.DOCUMENTS_CLIENT] ? '▼' : '►'}
                        </b>
                      </h5>
                    </Row>
                    {expandedSections[SECTION_KEYS.DOCUMENTS_CLIENT] && (
                      <div id='documents-data' className='mb-4'>
                        <Row className='mt-2 border-bottom pb-2'>
                          <Col className='col-sm-3'>
                            <b>{t('users')}</b>
                          </Col>
                          <Col className='col-sm-3'>
                            <b>{t('datumHochladen')}</b>
                          </Col>
                          <Col className='col-sm-3'>
                            <b>{t('file')}</b>
                          </Col>
                          <Col className='col-sm-3'>
                            <b>{t('actions')}</b>
                          </Col>
                        </Row>
                        {personData.filesClient.map((file, idx) => (
                          <Row key={idx} className='mt-2 border-bottom pb-2'>
                            <Col className='col-sm-3'>{file.user}</Col>
                            <Col className='col-sm-3'>{file.date}</Col>
                            <Col className='col-sm-3'>{file.name}</Col>
                            <Col className='col-sm-3'>
                              <Button
                                variant='outline-secondary'
                                size='sm'
                                className='me-1'
                                title={t('preview')}
                                onClick={() => handleFileView(file.name, 'stammdaten_filesClient')}
                              >
                                <i className='bi bi-eye'></i>
                              </Button>
                              <Button
                                variant='outline-primary'
                                size='sm'
                                className='me-1'
                                title={t('download')}
                                onClick={() =>
                                  handleFileDownload(file.name, 'stammdaten_filesClient')
                                }
                              >
                                <i className='bi bi-download'></i>
                              </Button>
                              {!isSharedClient && (
                                <Button
                                  id={`sign-${idx}`}
                                  variant='outline-secondary'
                                  size='sm'
                                  className='me-1'
                                  title={t('DS|Sign')}
                                  onClick={() => {
                                    setDocumentType('filesClient')
                                    requestSignAccess(file.name)
                                  }}
                                >
                                  <i className='bi bi-pencil-square'></i>
                                </Button>
                              )}
                            </Col>
                          </Row>
                        ))}
                        {isSharedClient && (
                          <Row className='my-4'>
                            <Col>
                              <input
                                className='do-not-save d-block mb-3'
                                type='file'
                                name='filesClient[]'
                                id='file'
                                multiple
                                required
                                onChange={handleFileChange}
                              ></input>
                              <Button
                                variant='success'
                                className='btn-sm pull-right'
                                type='submit'
                                onClick={() => handleUpload('filesClient')}
                              >
                                {t('Hochladen')}
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </div>
                    )}

                    {additionalForms?.some(f => f?.include) ? (
                      additionalForms
                        .filter(f => {
                          return f?.include && f.stammdatenType === 'person'
                        })
                        .map(schema => (
                          <>
                            <Row className='mt-2'>
                              <h5 className='bg-collapse text-white p-2 rounded mb-3 cursor-pointer d-flex align-items-center'>
                                <div
                                  className='d-flex align-items-center flex-grow-1'
                                  onClick={() => toggleSchema(schema._id)}
                                  role='button'
                                  aria-expanded={!!openSchemas[schema._id]}
                                  aria-controls={`schema-${schema._id}-content`}
                                >
                                  <PlusSquareFill />
                                  &nbsp;{schema.name}
                                  <b className='ms-auto'>{openSchemas[schema._id] ? '▼' : '►'}</b>
                                </div>
                                <Button
                                  variant='light'
                                  size='sm'
                                  className='ms-2'
                                  onClick={e => {
                                    e.stopPropagation()
                                    generateSingleFormPDF(schema._id, schema.name)
                                  }}
                                >
                                  PDF
                                </Button>
                              </h5>
                            </Row>

                            {(openSchemas[schema._id] || isGeneratingPDF) && (
                              <div id={`schema-${schema._id}-content`} className='mt-2'>
                                <div className='row g-3'>
                                  {schema.fields?.map(fld => {
                                    const fieldPath = `additional.dynamic.${schema.name}.${fld.name}`
                                    const currentValue = getDeep(personData, fieldPath)

                                    return (
                                      <div key={`${schema._id}:${fld.name}`} className='col-12'>
                                        <Form.Group className='mb-2'>
                                          {/* TEXT */}
                                          {fld.type === 'text' && (
                                            <>
                                              <Form.Label className='fw-semibold small text-muted'>
                                                {fld.name}
                                              </Form.Label>
                                              <FormControl
                                                className='form-control'
                                                name={`${schema.name}.${fld.name}`}
                                                id={fieldPath}
                                                type='text'
                                                value={currentValue || ''}
                                                onChange={handleChangeLocal}
                                              />
                                            </>
                                          )}

                                          {/* SELECT */}
                                          {fld.type === 'select' && (
                                            <>
                                              <Form.Label className='fw-semibold small text-muted'>
                                                {fld.name}
                                              </Form.Label>
                                              <FormSelect
                                                className='form-select'
                                                name={`${schema.name}.${fld.name}`}
                                                id={fieldPath}
                                                value={currentValue || ''}
                                                onChange={handleChangeLocal}
                                              >
                                                <option value=''>--</option>
                                                {fld.options?.map((opt: any) => (
                                                  <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                  </option>
                                                ))}
                                              </FormSelect>
                                            </>
                                          )}

                                          {/* CHECKBOX GROUP */}
                                          {fld.type === 'checkbox' && (
                                            <>
                                              <Form.Label className='fw-semibold small text-muted d-block mb-2'>
                                                {fld.name}
                                              </Form.Label>
                                              <div className='d-flex flex-column gap-2 border rounded p-3'>
                                                {fld.options?.map((opt: any, i: number) => {
                                                  const path = fieldPath + '.' + opt.value
                                                  const current = getDeep(personData, path)
                                                  return (
                                                    <Form.Check
                                                      key={`${schema._id}:${fld.name}:${opt.value}:${i}`}
                                                      type='checkbox'
                                                      id={path}
                                                      name={`${schema.name}.${fld.name}.${opt.value}`}
                                                      label={opt.label ?? opt}
                                                      checked={!!current}
                                                      onChange={handleChangeLocal}
                                                    />
                                                  )
                                                })}
                                              </div>
                                            </>
                                          )}

                                          {/* UPLOAD */}
                                          {fld.type === 'upload' && (
                                            <>
                                              <Form.Label className='fw-semibold small text-muted'>
                                                {fld.name}
                                              </Form.Label>
                                              <Row className='gy-2'>
                                                <Col xs={12}>
                                                  <input
                                                    className='do-not-save form-control'
                                                    type='file'
                                                    name='additionalFormFiles'
                                                    id='file'
                                                    multiple
                                                    required
                                                    onChange={handleFileChange}
                                                  />
                                                  <div className='form-text'>
                                                    {t('chooseFilesToUpload')}
                                                  </div>
                                                </Col>
                                                <Col xs='auto'>
                                                  <Button
                                                    variant='success'
                                                    className='btn-sm'
                                                    type='submit'
                                                    onClick={() =>
                                                      handleUpload('additionalFormFiles', fieldPath)
                                                    }
                                                  >
                                                    {t('Hochladen')}
                                                  </Button>
                                                </Col>
                                                <div id='documents-data' className='mb-4'>
                                                  <Row className='mt-2 border-bottom pb-2'>
                                                    <Col className='col-sm-3'>
                                                      <b>{t('users')}</b>
                                                    </Col>
                                                    <Col className='col-sm-3'>
                                                      <b>{t('datumHochladen')}</b>
                                                    </Col>
                                                    <Col className='col-sm-3'>
                                                      <b>{t('file')}</b>
                                                    </Col>
                                                    <Col className='col-sm-3'>
                                                      <b>{t('actions')}</b>
                                                    </Col>
                                                  </Row>
                                                  {toArray(currentValue).map(
                                                    (file: FileUpload, idx: number) => (
                                                      <Row
                                                        key={idx}
                                                        className='mt-2 border-bottom pb-2'
                                                      >
                                                        <Col className='col-sm-3'>{file.user}</Col>
                                                        <Col className='col-sm-3'>{file.date}</Col>
                                                        <Col className='col-sm-3'>{file.name}</Col>
                                                        <Col className='col-sm-3'>
                                                          <Button
                                                            variant='outline-secondary'
                                                            size='sm'
                                                            className='me-1'
                                                            title={t('preview')}
                                                            onClick={() =>
                                                              handleFileView(
                                                                file.name,
                                                                'stammdaten_additionalFormFiles'
                                                              )
                                                            }
                                                          >
                                                            <i className='bi bi-eye'></i>
                                                          </Button>
                                                          <Button
                                                            variant='outline-primary'
                                                            size='sm'
                                                            className='me-1'
                                                            title={t('download')}
                                                            onClick={() =>
                                                              handleFileDownload(
                                                                file.name,
                                                                'stammdaten_additionalFormFiles'
                                                              )
                                                            }
                                                          >
                                                            <i className='bi bi-download'></i>
                                                          </Button>
                                                          {!isSharedClient && (
                                                            <Button
                                                              id={`sign-${idx}`}
                                                              variant='outline-secondary'
                                                              size='sm'
                                                              className='me-1'
                                                              title={t('DS|Sign')}
                                                              onClick={() => {
                                                                setDocumentType('files')
                                                                requestSignAccess(file.name)
                                                              }}
                                                            >
                                                              <i className='bi bi-pencil-square'></i>
                                                            </Button>
                                                          )}
                                                        </Col>
                                                      </Row>
                                                    )
                                                  )}
                                                </div>
                                              </Row>
                                            </>
                                          )}

                                          {/* RADIO (Question/Answer) */}
                                          {fld.type === 'radio' && (
                                            <>
                                              <Form.Label className='fw-semibold small text-muted d-block mb-2'>
                                                {fld.name}
                                              </Form.Label>
                                              <div className='d-flex flex-column gap-2 border rounded p-3'>
                                                {fld.options?.map((opt: any, i: number) => {
                                                  return (
                                                    <Form.Check
                                                      key={`${schema._id}:${fld.name}:${opt.label}:${i}`}
                                                      type='radio'
                                                      id={`${fieldPath}.${opt.label}`}
                                                      name={`${schema.name}.${fld.name}`}
                                                      label={opt.label ?? opt}
                                                      value={opt.label}
                                                      checked={currentValue === opt.label}
                                                      onChange={handleChangeLocal}
                                                    />
                                                  )
                                                })}
                                              </div>
                                            </>
                                          )}
                                        </Form.Group>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        ))
                    ) : (
                      <p className='text-muted'>
                        {t('noAdditionalSchemas') || 'No additional schemas'}
                      </p>
                    )}

                    <hr />
                    <Row className='mt-2'>
                      <KSwitch
                        label={t('stammdatenConfirm')}
                        initialValue={validationModal.agreed}
                        onChange={handleAgreementChange}
                      />
                    </Row>
                  </Card.Body>
                </Card>
              </div>
            </fieldset>
          </form>
        </Row>
      </Container>

      {signModal && (
        <KSignModal
          show={signModal}
          onClose={handleClose}
          onAccept={handleClose}
          firmename={personData?.name || ''}
          documentName={currentDocument}
          client={personData || null}
          stammdaten={true}
          stammdatenType={documentType}
        />
      )}
      <HistoryModal
        open={historyModal.isOpen}
        onHide={() =>
          setHistoryModal({
            isOpen: false,
            title: historyModal.title,
            entries: historyModal.entries,
            options: historyModal.options,
          })
        }
        entries={historyModal.entries}
        title={historyModal.title}
        options={historyModal.options}
      />

      <ValidationModal
        open={validationModal.isOpen}
        onHide={() =>
          setValidationModal({
            isOpen: false,
            field: validationModal.field,
            agreed: validationModal.agreed,
          })
        }
        field={validationModal.field}
      />

      {!costConfirmed && (
        <KConfirm
          show={!costConfirmed}
          message={
            <>
              {t('stammdatenCostWarning')}
              {serviceCosts?.stammdaten ? (
                <div className='small mt-1'>
                  <strong>{serviceCosts.stammdaten}</strong>{' '}
                  <strong>{t('punkteWerdenVerrechnet')}</strong>
                </div>
              ) : null}
            </>
          }
          onConfirm={() => setCostConfirmed(true)}
          onCancel={() => navigate('/client/stammdaten')}
        />
      )}

      {access && (
        <KAccess
          show={access}
          pin={pin}
          title={t('pinCodeSent')}
          onConfirm={(pin: any) => {
            checkPin(pin)
          }}
          onClose={() => setAccess(false)}
        />
      )}
      {accessForSign && (
        <KAccess
          show={accessForSign}
          title={pinRequired ? t('pinCodeSent') : t('confirmAction')}
          pin={pin}
          pinRequired={pinRequired}
          onConfirm={(pin: any) => {
            checkPinForSign(pin)
          }}
          onClose={() => setAccessForSign(false)}
        />
      )}

      <KFilePreview target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </>
  )
}
